import React, { FC, useState } from 'react'
import { Button, Tooltip, TooltipContentWrapper, Text } from 'ui';
import { relewiseFetcher, RelewiseProduct, IFetchRelewiseProps } from '~/components/features';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { useRouter } from 'next/router';
import { useNotification } from '~/shared/hooks/useNotification';
import useFacets from '~/components/features/ProductsList/Facets/use-facets';
import { useUserInformation } from '~/page-type/page-types/P181Profile';
import { generatePDPLink } from '~/shared/utils';
import { isJPG, getHostUrl } from 'utils';
import { useAuthentication, usePrefixUrl } from '~/shared/hooks';
import { ProductSearchExport } from 'api-types/src/data-contract/auto-generated/catalogue-model';
import { useTranslations } from '~/services/cms';

const isJPGroup = isJPG();
const host = getHostUrl();

import styles from './DownloadProductList.module.css';
import clsx from 'clsx';

type ExcelData = RelewiseProduct & {
    commerceToolsData: ProductSearchExport;
}

type MapExcelDataProps = {
    relewiseData: RelewiseProduct[];
    commerceToolsData: ProductSearchExport[];
    locale: string;
}

const mapExcelData = (props: MapExcelDataProps) => {


    const { relewiseData, commerceToolsData, locale } = props

    const data: ExcelData[] = relewiseData.map(product => {
        return ({
            ...product,
            commerceToolsData: commerceToolsData.find((item: any) => item.jpNo === product?.data?.JpGroupNumber?.value) || {}
        })
    })
    const excelDataPromises = data.map(async (product) => {
        const {
            brand,
            displayName,
            productId,
            data,
            commerceToolsData
        } = product;

        const PDPLink = generatePDPLink({
            productId,
            variantId: productId,
            displayName: displayName || 'product'
        });

        return ({
            "Month": data?.NewMonth?.value,
            "JP No.": data?.JpGroupNumber?.value,
            "JP OE No.": data?.OldJPNumber?.value,
            "OE 1": commerceToolsData?.oe ? commerceToolsData?.oe[0] || '' : '',
            "OE 2": commerceToolsData?.oe ? commerceToolsData?.oe[1] || '' : '',
            "OE 3": commerceToolsData?.oe ? commerceToolsData?.oe[2] || '' : '',
            "Description": displayName,
            "Car Model": commerceToolsData?.carModel ?? '',
            "Engine": commerceToolsData?.engine ?? '',
            "Year": commerceToolsData?.year ?? '',
            "Brand": brand?.displayName || '',
            "Net price1 excl. VAT": commerceToolsData?.priceBeforeTax || '',
            "Unit": data?.Unit?.value,
            "REM": data?.Remark?.value,
            "MOQ": data?.PackingUnit?.value,
            "Nett Weight in KG": commerceToolsData?.weight || '',
            "Weight Pr Pack Unit in Kg": commerceToolsData?.weightPrPackUnitInKg || '',
            "Width in mm": data?.Width?.value,
            "Height in mm": data?.Height?.value,
            "Depth in mm": data?.Depth?.value,
            "Express shipping possible": commerceToolsData?.expressShippingPossibility || '',
            "EAN13": data?.Barcode?.value,
            "Image": data?.PictureWebFull?.value,
            "ImageThumbnail": data?.PictureWebFull?.value,
            "ProductLink": `${host}/${locale}${PDPLink}`
        })

    })
    return Promise.all(excelDataPromises);
}

type Props = Omit<IFetchRelewiseProps, 'activeFacets' | 'disallowedProductsGroups' | 'locale'> & {
    searchQuery?: string;
    vehicleTypeNumberFilter?: number[];
    totalProductsCount?: number;
}

const DownloadProductList: FC<Props> = (props) => {
    const {
        searchQuery,
        vehicleTypeNumberFilter,
        totalProductsCount = 2000,
        productCategories = []
    } = props
    const translate = useTranslations();

    const [isFetching, setIsFetching] = useState(false)

    const { push } = useNotification();
    const { locale, defaultLocale } = useRouter();
    const { data: userInformation } = useUserInformation()
    const { activeFacets } = useFacets()
    const { catalogueBaseUrl } = usePrefixUrl()
    const { isAuthenticated, session } = useAuthentication();

    const disallowedProductsGroups = userInformation?.disallowedProductsGroups || []

    const fetchRelewise = async () => {
        return relewiseFetcher({
            locale: locale ?? defaultLocale ?? 'da',
            searchQuery,
            activeFacets,
            pageSize: totalProductsCount,
            vehicleTypeNumberFilter,
            disallowedProductsGroups,
            includeFacets: true,
            page: 1,
            productCategories
        })
    }

    const fetchCommerceToolsData = async (ids: string[]) => {
        const url = `${catalogueBaseUrl}/exports`
        const response = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(ids),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.accessToken}`
            }
        })
        return await response?.json()
    }

    const downloadFile = async () => {
        setIsFetching(true)
        try {
            const relewiseData = await fetchRelewise()
            const ids = relewiseData?.results?.map((product: any) => product.productId) || []
            const commerceToolsData = await fetchCommerceToolsData(ids)

            const excelData = await mapExcelData({
                relewiseData: relewiseData?.results as RelewiseProduct[],
                commerceToolsData: commerceToolsData as any[],
                locale: locale ?? defaultLocale ?? 'da'
            })

            downloadExcel(excelData)

        } catch (error) {
            console.log(error);
            setIsFetching(false);
            push({ severity: 'warning', text: 'Something went wrong' })
        }
    }

    const downloadExcel = async (data: any[]) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Product List');

        // Add headers
        const headers = Object.keys(data[0]);
        worksheet.addRow(headers);

        for (const item of data) {
            const rowValues = headers.map(header => {
                return header !== 'ImageThumbnail' ? item[header] || '' : ''; // Placeholder for image column
            });
            const rowIndex = worksheet.addRow(rowValues).number;

            if (item.ImageThumbnail) {
                try {
                    // Fetch the image and convert it to a base64 string
                    const response = await fetch('https://www.jpgroup.dk/cdn-cgi/image/width=75,format=auto,quality=90/' + item.ImageThumbnail);
                    const blob = await response.blob();
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    await new Promise(resolve => {
                        reader.onloadend = () => {
                            const base64data = reader.result;
                            const extension = blob.type.split('/')[1]; // Extract extension from MIME type
                            const imageId = workbook.addImage({
                                base64: base64data as string,
                                extension: extension as any,
                            });

                            // Adjust the image placement
                            const imageColIndex = headers.indexOf('ImageThumbnail') + 1;
                            const startRow = rowIndex; // Already 1-based index
                            worksheet.getRow(startRow).height = 80; // height
                            worksheet.getColumn(imageColIndex).width = 15; // width

                            // Place the image on the worksheet using the imageId
                            worksheet.addImage(imageId, {
                                tl: { col: imageColIndex - 0.1, row: startRow - 0.7 },
                                ext: { width: 90, height: 90 }
                            });

                            resolve(true);
                        };
                    });
                } catch (error) {
                    console.error('Failed to load image:', error);
                }
            }
        }

        // Save workbook
        workbook.xlsx.writeBuffer().then((buffer: BlobPart) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, 'DataSheet.xlsx');
            setIsFetching(false);
            push({ severity: 'success', text: translate('productPage.exportFileSuccessfullyGenerated') })
        }).catch((err: any) => {
            console.error('Error writing workbook:', err);
            setIsFetching(false);
        });
    };


    const shouldRender = isJPGroup && isAuthenticated

    if (!shouldRender) {
        return null
    }

    return (
        <Tooltip
            placement='bottom'
            content={totalProductsCount > 2000 ?
                (<TooltipContentWrapper>
                    <Text variant='bodyExtraSmall'>
                        {translate('productPage.exportLimitMessage')}
                    </Text>
                </TooltipContentWrapper>) : null
            }>
            <Button
                onClick={() => downloadFile()}
                isLoading={isFetching}
                className={clsx({
                    [styles.buttonIsLoading]: isFetching
                })}
                disabled={isFetching || totalProductsCount > 2000}
                iconName='download'
                size='small'
                variant='secondary'>
                {isFetching ? translate('productPage.generatingExportFile') : ''}
            </Button>
        </Tooltip>
    )
}

export default DownloadProductList